import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

export default function Cursor() {
	const cursorRef = useRef(null);
	const pointerRef = useRef({ x: 0, y: 0, hasMoved: false });
	const rafTween = useRef(null);
	const lastInteractiveRef = useRef(null); // garde l'élément interactif actuel
	const enabledRef = useRef(false);
	const handlersRef = useRef({});

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const supportsHover = () => {
			try {
				if (!window.matchMedia) return false;
				return window.matchMedia('(any-hover: hover)').matches || window.matchMedia('(pointer: fine)').matches;
			} catch (e) {
				return false;
			}
		};

		const setup = () => {
			if (enabledRef.current) return;
			if (!supportsHover()) return;

			const el = cursorRef.current;
			if (!el) return;

			document.documentElement.classList.add('js-has-hover');

			// Ne pas afficher par défaut
			el.classList.remove('cursor--visible');

			const showCursor = () => {
				if (!el.classList.contains('cursor--visible')) el.classList.add('cursor--visible');
			};
			const hideCursor = () => {
				if (el.classList.contains('cursor--visible')) el.classList.remove('cursor--visible');
				if (el.classList.contains('cursor--hover')) el.classList.remove('cursor--hover');
				lastInteractiveRef.current = null;
			};

			const previousCursor = document.documentElement.style.cursor;
			document.documentElement.style.cursor = 'none';

			const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

			// Handlers
			const onPointerMove = (e) => {
				pointerRef.current.x = e.clientX;
				pointerRef.current.y = e.clientY;
				pointerRef.current.hasMoved = true;
				showCursor();
				if (prefersReduced) {
					el.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0) translate(-50%, -50%)`;
				}
			};

			const tick = () => {
				const { x, y } = pointerRef.current;
				gsap.to(el, { x: x, y: y, duration: 0.08, ease: 'power3.out', overwrite: 'auto' });
			};

			rafTween.current = () => {
				tick();
				requestAnimationFrame(rafTween.current);
			};

			if (!prefersReduced) requestAnimationFrame(rafTween.current);

			const hoverSelector = 'a:not(.disabled), button, input[type="button"], input[type="submit"], [role="button"], [data-cursor-hover], .cursor-event, .event-cursor';

			const isInteractiveAt = (x, y) => {
				if (typeof document.elementFromPoint !== 'function') return false;
				let node = null;
				const prevDisplay = el.style.display;
				try {
					el.style.display = 'none';
					node = document.elementFromPoint(x, y);
				} catch (err) {
					// ignore
				} finally {
					el.style.display = prevDisplay;
				}
				return node && node.closest && Boolean(node.closest(hoverSelector));
			};

			const updateHoverStateFromPoint = () => {
				const { x, y, hasMoved } = pointerRef.current;
				if (!hasMoved) return;
				const interactive = isInteractiveAt(x, y);
				if (interactive) {
					if (!el.classList.contains('cursor--hover')) el.classList.add('cursor--hover');
				} else {
					if (el.classList.contains('cursor--hover')) el.classList.remove('cursor--hover');
				}
			};

			const onPointerOver = (e) => {
				const target = e.target;
				const interactive = target && target.closest && target.closest(hoverSelector);
				if (interactive) {
					lastInteractiveRef.current = interactive;
					if (typeof e.clientX === 'number' && typeof e.clientY === 'number') {
						pointerRef.current.x = e.clientX;
						pointerRef.current.y = e.clientY;
						pointerRef.current.hasMoved = true;
					}
					showCursor();
					if (!el.classList.contains('cursor--hover')) {
						el.classList.add('cursor--hover');
					}
				}
			};

			const onPointerOut = (e) => {
				const to = e.relatedTarget;
				if (to && to.closest && to.closest(hoverSelector)) return;
				if (lastInteractiveRef.current) lastInteractiveRef.current = null;
				if (el.classList.contains('cursor--hover')) el.classList.remove('cursor--hover');
				if (!to) hideCursor();
			};

			const onScrollOrResize = () => updateHoverStateFromPoint();
			const onWindowMouseOut = (e) => { if (e && !e.relatedTarget) hideCursor(); };
			const onDocPointerLeave = () => hideCursor();

			// Save handlers and state for teardown
			handlersRef.current = {
				el,
				previousCursor,
				onPointerMove,
				onPointerOver,
				onPointerOut,
				onScrollOrResize,
				onWindowMouseOut,
				onDocPointerLeave,
				hideCursor,
				showCursor,
				prefersReduced,
			};

			document.addEventListener('pointermove', onPointerMove, { passive: true });
			document.addEventListener('pointerover', onPointerOver, { passive: true });
			document.addEventListener('pointerout', onPointerOut, { passive: true });
			window.addEventListener('scroll', onScrollOrResize, { passive: true });
			window.addEventListener('resize', onScrollOrResize, { passive: true });
			window.addEventListener('mouseout', onWindowMouseOut, { passive: true });
			window.addEventListener('blur', hideCursor);
			document.addEventListener('pointerleave', onDocPointerLeave, { passive: true });

			enabledRef.current = true;
		};

		const teardown = () => {
			if (!enabledRef.current) return;
			const h = handlersRef.current || {};
			const el = h.el;
			if (h.onPointerMove) document.removeEventListener('pointermove', h.onPointerMove);
			if (h.onPointerOver) document.removeEventListener('pointerover', h.onPointerOver);
			if (h.onPointerOut) document.removeEventListener('pointerout', h.onPointerOut);
			if (h.onScrollOrResize) window.removeEventListener('scroll', h.onScrollOrResize);
			if (h.onScrollOrResize) window.removeEventListener('resize', h.onScrollOrResize);
			if (h.onWindowMouseOut) window.removeEventListener('mouseout', h.onWindowMouseOut);
			if (h.hideCursor) window.removeEventListener('blur', h.hideCursor);
			if (h.onDocPointerLeave) document.removeEventListener('pointerleave', h.onDocPointerLeave);

			// stop raf loop
			rafTween.current = null;

			// retirer la classe HTML
			document.documentElement.classList.remove('js-has-hover');

			// remettre le curseur natif
			document.documentElement.style.cursor = h.previousCursor || '';

			// kill tweens
			if (el) gsap.killTweensOf(el);

			enabledRef.current = false;
			handlersRef.current = {};
		};

		// initial setup
		if (supportsHover()) setup();

		// écoute dynamique des changements de media queries et du resize
		const mqlHover = window.matchMedia('(any-hover: hover)');
		const mqlPointer = window.matchMedia('(pointer: fine)');
		const handleMqlChange = () => {
			if (supportsHover()) setup(); else teardown();
		};

		if (mqlHover.addEventListener) mqlHover.addEventListener('change', handleMqlChange);
		else mqlHover.addListener(handleMqlChange);
		if (mqlPointer.addEventListener) mqlPointer.addEventListener('change', handleMqlChange);
		else mqlPointer.addListener(handleMqlChange);

		let resizeTimer = null;
		const onResize = () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				if (supportsHover()) setup(); else teardown();
			}, 150);
		};
		window.addEventListener('resize', onResize, { passive: true });

		// cleanup général
		return () => {
			if (mqlHover.removeEventListener) mqlHover.removeEventListener('change', handleMqlChange);
			else mqlHover.removeListener(handleMqlChange);
			if (mqlPointer.removeEventListener) mqlPointer.removeEventListener('change', handleMqlChange);
			else mqlPointer.removeListener(handleMqlChange);
			window.removeEventListener('resize', onResize);
			clearTimeout(resizeTimer);
			// teardown si nécessaire
			teardown();
		};
	}, []);

	// Render
	return (
		<div
			ref={cursorRef}
			aria-hidden="true"
			className="cursor"
		>
				<div className="cursor-element"/>
		</div>
	);
}